import React from 'react';
import { StandardPdfTemplate } from '../../templates/StandardPdfTemplate';
import { ProjectData, ProjectInfoData } from '../../shared/types/PdfCommonTypes';
import { TFunction } from 'i18next';
import { IGeogridData, IDynamicProjectOutputData, IProject } from '../../../../api/IApiResponse';
import { IInitialState } from '../../../../../reduxService';
import { IAirportImages } from '../../../../services/PdfImageCaptureService';
import { AirportResultsCore } from './sections/AirportResultsCore';
import { AirportGeoGridCore } from './sections/AirportGeoGridCore';

export interface IAirportCombinedResultsPdfDocumentProps {
    dynamicProject: IInitialState<IProject>;
    output: IDynamicProjectOutputData | undefined;
    images: IAirportImages;
    geoGridData?: IGeogridData;
    geogridImage?: string;
    t: TFunction;
}

const AirportCombinedResultsPdfDocumentV2: React.FC<IAirportCombinedResultsPdfDocumentProps> = ({ dynamicProject, output, images, geoGridData, geogridImage, t }) => {
    const projectData = dynamicProject?.data;
    const projectType = projectData?.type || 'airport';
    const unitSystem = projectData?.unit || 'Metric';

    // Prepare data for StandardPdfTemplate
    const projectDataForTemplate: ProjectData = {
        projectName: projectData?.projectName || 'N/A',
        projectType: projectType,
        unit: unitSystem,
        mapImage: projectData?.mapImage,
    };

    const projectInfoDataForTemplate: ProjectInfoData = {
        data: {
            nrProject: projectData?.projectInfo?.data?.nrProject || 'N/A',
        },
        projectOwner: projectData?.projectInfo?.projectOwner || 'N/A',
        designerFirmName: projectData?.projectInfo?.designerFirmName || 'N/A',
        designerFirmCountry: projectData?.projectInfo?.designerFirmCountry || 'N/A',
        designerFirmCity: projectData?.projectInfo?.data?.designerFirmCity || 'N/A',
        coordinates: projectData?.projectInfo?.coordinates || [null, null],
    };

    return (
        <StandardPdfTemplate
            projectType={projectType}
            projectData={projectDataForTemplate}
            projectInfoData={projectInfoDataForTemplate}
            t={t}
            headerConfig={{
                customTitle: t('pdf_header_technical_report_groundmac') || 'GROUNDMAC - Design Technical Report',
                customProjectType: t('project_type_airport') || 'Airport',
                showGeogridSubtitle: true,
            }}
            footerConfig={{
                customText: t('pdf_footer') || 'MACread eDesign',
                customNotice: t('pdf_footer_notice') || 'Footer notice',
            }}
            showProjectInfo={true}>
            {/* Use the shared core component for Airport Results */}
            <AirportResultsCore
                // ✅ ISSUE-001 FIX: Provide fallbacks for missing images
                images={images || {
                    tireImageUnstabilized: '',
                    tireImageIncreasedPassages: '',
                    tireImageGeogrid: '',
                    chartImg: '',
                }}
                projectData={projectData}
                output={output}
                aashtoResults={projectData?.aashtoResults}
                t={t}
                showInputData={true}
                showResults={true}
                customTitle="Combined Project & GeoGrid Results"
            />

            {/* Use the shared core component for GeoGrid Results */}
            <AirportGeoGridCore
                geoGridData={geoGridData}
                geogridImage={geogridImage}
                projectData={projectData}
                t={t}
                showInputData={true}
                showGeoGridImage={true}
                showCourseResults={true}
                customTitle="GeoGrid Reinforcement Analysis"
            />
        </StandardPdfTemplate>
    );
};

export default AirportCombinedResultsPdfDocumentV2;
